// frontend/src/pages/superadmin/DetectionsViewer.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Plane, Car, Shield, Wifi, WifiOff,
  RefreshCw, Trash2, Eye,
  Activity, Camera, Clock, CheckCircle, XCircle,
  Search, Zap
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const POLL_INTERVAL = 6000;

// ─── API helpers avec axios (avec token JWT) ─────────────────────────────────
const apiFetch = async (path, opts = {}) => {
  const url = `${API_BASE}/surveillance${path}`;
  
  // Récupérer le token JWT depuis localStorage (comme dans AuthContext)
  const token = localStorage.getItem('access_token');
  
  try {
    const config = {
      method: opts.method || 'GET',
      url: url,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        ...opts.headers,
      },
    };
    
    // Ajouter le token JWT si présent
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    if (opts.body) {
      config.data = opts.body;
    }
    
    const response = await axios(config);
    return response.data;
  } catch (error) {
    console.error('[API Error]', error);
    if (error.response?.status === 401) {
      // Token expiré ou invalide
      window.location.href = '/login';
    }
    throw new Error(error.response?.status || error.message);
  }
};

// ─── Helpers visuels ──────────────────────────────────────────
const statusConfig = {
  confirmed_landing:   { label: 'Landing ✓',  color: '#22d3ee', bg: 'rgba(34,211,238,0.12)'  },
  confirmed_takeoff:   { label: 'Takeoff ✓',  color: '#a78bfa', bg: 'rgba(167,139,250,0.12)' },
  authorized_vehicle:  { label: 'Authorized', color: '#4ade80', bg: 'rgba(74,222,128,0.12)'  },
  unknown_vehicle:     { label: 'Unknown Veh',color: '#fb923c', bg: 'rgba(251,146,60,0.12)'  },
  unknown_aircraft:    { label: 'Unknown A/C',color: '#fbbf24', bg: 'rgba(251,191,36,0.12)'  },
  pending:             { label: 'Pending',    color: '#94a3b8', bg: 'rgba(148,163,184,0.12)' },
  error:               { label: 'Error',      color: '#f87171', bg: 'rgba(248,113,113,0.12)' },
};

const StatusBadge = ({ status }) => {
  const cfg = statusConfig[status] || statusConfig.pending;
  return (
    <span style={{
      color: cfg.color, background: cfg.bg,
      padding: '2px 8px', borderRadius: 4,
      fontSize: 11, fontWeight: 600, letterSpacing: '0.03em',
      border: `1px solid ${cfg.color}33`,
      fontFamily: 'monospace',
    }}>
      {cfg.label}
    </span>
  );
};

const ConfidenceBar = ({ value }) => {
  const pct = Math.round(value * 100);
  const color = pct >= 80 ? '#4ade80' : pct >= 60 ? '#fbbf24' : '#f87171';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{
        width: 56, height: 4, background: '#1e293b',
        borderRadius: 2, overflow: 'hidden',
      }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 2 }} />
      </div>
      <span style={{ fontSize: 11, color, fontFamily: 'monospace' }}>{pct}%</span>
    </div>
  );
};

const EntityIcon = ({ type, size = 14 }) => {
  if (type === 'aircraft')
    return <Plane style={{ width: size, height: size, color: '#60a5fa' }} />;
  if (type === 'vehicle')
    return <Car style={{ width: size, height: size, color: '#a78bfa' }} />;
  return <Activity style={{ width: size, height: size, color: '#94a3b8' }} />;
};

const formatTs = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

const formatDelta = (s) => {
  if (s == null) return null;
  if (s < 60) return `${s}s`;
  return `${Math.round(s / 60)}m${s % 60}s`;
};

// ─── KPI Card ─────────────────────────────────────────────────
const KpiCard = ({ label, value, sub, icon: Icon, color = '#60a5fa', pulse = false }) => (
  <div style={{
    background: 'rgba(15,23,42,0.7)',
    border: `1px solid ${color}30`,
    borderRadius: 10,
    padding: '14px 18px',
    display: 'flex', flexDirection: 'column', gap: 4,
    position: 'relative', overflow: 'hidden',
  }}>
    {pulse && (
      <span style={{
        position: 'absolute', top: 10, right: 10,
        width: 8, height: 8, borderRadius: '50%',
        background: color, boxShadow: `0 0 8px ${color}`,
        animation: 'pulse 1.5s infinite',
      }} />
    )}
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {Icon && <Icon style={{ width: 14, height: 14, color }} />}
      <span style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </span>
    </div>
    <span style={{ fontSize: 26, fontWeight: 700, color, lineHeight: 1.1, fontFamily: 'monospace' }}>
      {value ?? '—'}
    </span>
    {sub && <span style={{ fontSize: 11, color: '#475569' }}>{sub}</span>}
  </div>
);

// ─── Live Feed Row ─────────────────────────────────────────────
const LiveRow = ({ det, isNew, onSelect }) => {
  const isAircraft = det.entity_type === 'aircraft';
  const accent = isAircraft ? '#60a5fa' : '#a78bfa';

  return (
    <div
      onClick={() => onSelect(det)}
      style={{
        display: 'grid',
        gridTemplateColumns: '18px 1fr 100px 80px 90px 80px',
        gap: '0 12px',
        alignItems: 'center',
        padding: '9px 14px',
        borderBottom: '1px solid #0f172a',
        cursor: 'pointer',
        background: isNew ? `${accent}08` : 'transparent',
        transition: 'background 0.3s',
        borderLeft: isNew ? `2px solid ${accent}` : '2px solid transparent',
      }}
      onMouseEnter={e => e.currentTarget.style.background = '#1e293b55'}
      onMouseLeave={e => e.currentTarget.style.background = isNew ? `${accent}08` : 'transparent'}
    >
      <EntityIcon type={det.entity_type} size={13} />

      <div>
        <span style={{
          fontFamily: 'monospace', fontSize: 13, fontWeight: 600,
          color: accent, letterSpacing: '0.04em',
        }}>
          {det.tail_number || det.plate_number || '???'}
        </span>
        {det.owner_name && (
          <span style={{ fontSize: 11, color: '#64748b', marginLeft: 8 }}>
            {det.owner_name}
          </span>
        )}
        {(det.veh_make || det.aircraft_type) && (
          <span style={{ fontSize: 10, color: '#475569', marginLeft: 6 }}>
            {det.veh_make
              ? `${det.veh_make} ${det.veh_model || ''} ${det.veh_year || ''}`
              : det.aircraft_type}
          </span>
        )}
      </div>

      <span style={{ fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}>
        <Camera style={{ width: 10, height: 10 }} />
        {det.camera_name || '—'}
      </span>

      <ConfidenceBar value={det.confidence || 0} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {det.adsb_matched && (
          <span style={{ fontSize: 10, color: '#22d3ee', display: 'flex', alignItems: 'center', gap: 3 }}>
            <Wifi style={{ width: 9, height: 9 }} />
            ADS-B {det.adsb_movement_type || ''}
            {det.adsb_match_delta_s != null && (
              <span style={{ color: '#475569' }}> Δ{formatDelta(det.adsb_match_delta_s)}</span>
            )}
          </span>
        )}
        {det.gate_authorized && (
          <span style={{ fontSize: 10, color: '#4ade80', display: 'flex', alignItems: 'center', gap: 3 }}>
            <Shield style={{ width: 9, height: 9 }} /> Gate ✓
          </span>
        )}
        {det.owner_matched && !det.gate_authorized && (
          <span style={{ fontSize: 10, color: '#4ade80', display: 'flex', alignItems: 'center', gap: 3 }}>
            <CheckCircle style={{ width: 9, height: 9 }} /> Owner ✓
          </span>
        )}
      </div>

      <span style={{ fontSize: 10, color: '#475569', fontFamily: 'monospace' }}>
        {formatTs(det.timestamp)}
      </span>
    </div>
  );
};

// ─── Detail Panel ──────────────────────────────────────────────
const DetailPanel = ({ det, onClose }) => {
  if (!det) return null;
  const isAircraft = det.entity_type === 'aircraft';
  const accent = isAircraft ? '#60a5fa' : '#a78bfa';

  const Field = ({ label, value, mono = false }) => (
    value ? (
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>
          {label}
        </div>
        <div style={{
          fontSize: 13, color: '#e2e8f0',
          fontFamily: mono ? 'monospace' : 'inherit',
        }}>
          {value}
        </div>
      </div>
    ) : null
  );

  return (
    <div style={{
      width: 320, borderLeft: '1px solid #1e293b',
      background: 'rgba(2,6,23,0.95)',
      padding: '18px 16px', overflowY: 'auto',
      flexShrink: 0,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <EntityIcon type={det.entity_type} size={16} />
          <span style={{ fontSize: 15, fontWeight: 700, color: accent, fontFamily: 'monospace' }}>
            {det.tail_number || det.plate_number || 'UNKNOWN'}
          </span>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', padding: 4 }}>
          <XCircle style={{ width: 16, height: 16 }} />
        </button>
      </div>

      <div style={{ marginBottom: 14 }}>
        <StatusBadge status={
          det.adsb_matched ? (det.adsb_movement_type === 'TAKEOFF' ? 'confirmed_takeoff' : 'confirmed_landing')
          : det.gate_authorized ? 'authorized_vehicle'
          : det.entity_type === 'vehicle' && !det.owner_matched ? 'unknown_vehicle'
          : det.entity_type === 'aircraft' && !det.adsb_matched ? 'unknown_aircraft'
          : 'pending'
        } />
      </div>

      {det.image_path && !det.image_deleted && (
        <div style={{ marginBottom: 14 }}>
          <img
            src={`/uploads/agent_detections/${det.image_path}`}
            alt="detection"
            style={{
              width: '100%', borderRadius: 8,
              border: `1px solid ${accent}30`,
              objectFit: 'cover', maxHeight: 180,
            }}
            onError={e => { e.target.style.display = 'none'; }}
          />
        </div>
      )}
      {det.image_deleted && (
        <div style={{
          background: '#1e293b', borderRadius: 8, padding: '10px 12px',
          marginBottom: 14, fontSize: 11, color: '#475569',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <Trash2 style={{ width: 12, height: 12 }} />
          Image deleted (retention policy)
        </div>
      )}

      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 10, color: '#334155', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.06em' }}>
          Detection
        </div>
        <Field label="Timestamp" value={formatTs(det.timestamp)} />
        <Field label="Camera" value={det.camera_name} />
        <Field label="Confidence" value={`${Math.round((det.confidence || 0) * 100)}%`} />
        <Field label="Event type" value={det.event_type} mono />
        {det.processing_notes && <Field label="Notes" value={det.processing_notes} />}
      </div>

      {isAircraft && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: '#334155', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.06em' }}>
            ADS-B Correlation
          </div>
          {det.adsb_matched ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <Wifi style={{ width: 13, height: 13, color: '#22d3ee' }} />
                <span style={{ fontSize: 12, color: '#22d3ee', fontWeight: 600 }}>
                  {det.adsb_movement_type} confirmed
                </span>
              </div>
              <Field label="ADS-B timestamp" value={formatTs(det.adsb_movement_ts)} />
              {det.adsb_match_delta_s != null && (
                <Field label="Time delta" value={formatDelta(det.adsb_match_delta_s)} />
              )}
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#475569', fontSize: 12 }}>
              <WifiOff style={{ width: 13, height: 13 }} />
              No ADS-B match in ±10m window
            </div>
          )}
          {det.aircraft_type && <Field label="Aircraft type" value={det.aircraft_type} />}
          {det.aircraft_model && <Field label="Model" value={`${det.manufacturer || ''} ${det.aircraft_model}`} />}
        </div>
      )}

      {det.owner_matched && det.owner_name && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: '#334155', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.06em' }}>
            Owner
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <CheckCircle style={{ width: 13, height: 13, color: '#4ade80' }} />
            <span style={{ fontSize: 13, color: '#4ade80', fontWeight: 600 }}>{det.owner_name}</span>
          </div>
          <Field label="Email" value={det.owner_email} />
        </div>
      )}

      {!isAircraft && (det.veh_make || det.veh_model) && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: '#334155', textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.06em' }}>
            Vehicle Info ({det.veh_api_source || 'API'})
          </div>
          <Field label="Make" value={det.veh_make} />
          <Field label="Model" value={det.veh_model} />
          <Field label="Year" value={det.veh_year} />
          <Field label="Color" value={det.veh_color} />
          <Field label="Type" value={det.vehicle_type} />
        </div>
      )}

      {det.gate_authorized && (
        <div style={{
          background: 'rgba(74,222,128,0.08)',
          border: '1px solid rgba(74,222,128,0.25)',
          borderRadius: 8, padding: '10px 12px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <Shield style={{ width: 13, height: 13, color: '#4ade80' }} />
            <span style={{ fontSize: 12, color: '#4ade80', fontWeight: 600 }}>Gate Authorized</span>
          </div>
          {det.gate_triggered_at && (
            <div style={{ fontSize: 11, color: '#475569' }}>
              Triggered: {formatTs(det.gate_triggered_at)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────
const DetectionsViewer = () => {
  const [stats, setStats] = useState(null);
  const [liveFeed, setLiveFeed] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liveOn, setLiveOn] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('live');
  const [processed, setProcessed] = useState([]);
  const [vehicleLookups, setVehicleLookups] = useState([]);
  const [filter, setFilter] = useState({ entity: '', status: '' });
  const [search, setSearch] = useState('');
  const [newIds, setNewIds] = useState(new Set());
  const prevLiveIds = useRef(new Set());
  const pollRef = useRef(null);

  const fetchStats = useCallback(async () => {
    try {
      const data = await apiFetch('/detections/stats?days=7');
      setStats(data);
    } catch (e) {
      console.error('[Stats]', e);
    }
  }, []);

  const fetchLive = useCallback(async () => {
    try {
      const data = await apiFetch('/detections/live?limit=60');
      const incoming = Array.isArray(data) ? data : [];

      const freshIds = new Set();
      incoming.forEach(d => {
        if (!prevLiveIds.current.has(d.id)) freshIds.add(d.id);
      });
      prevLiveIds.current = new Set(incoming.map(d => d.id));

      setNewIds(freshIds);
      setLiveFeed(incoming);

      if (freshIds.size > 0) {
        setTimeout(() => setNewIds(new Set()), 4000);
      }
    } catch (e) {
      console.error('[Live]', e);
    }
  }, []);

  const fetchProcessed = useCallback(async () => {
    try {
      const data = await apiFetch('/processed?limit=100');
      setProcessed(data.items || []);
    } catch (e) { console.error('[Processed]', e); }
  }, []);

  const fetchVehicleLookups = useCallback(async () => {
    try {
      const data = await apiFetch('/vehicle-lookups?limit=100');
      setVehicleLookups(Array.isArray(data) ? data : []);
    } catch (e) { console.error('[VehicleLookups]', e); }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchLive()]);
      setLoading(false);
    };
    init();
  }, [fetchStats, fetchLive]);

  useEffect(() => {
    if (liveOn) {
      pollRef.current = setInterval(() => {
        fetchLive();
        fetchStats();
      }, POLL_INTERVAL);
    }
    return () => clearInterval(pollRef.current);
  }, [liveOn, fetchLive, fetchStats]);

  useEffect(() => {
    if (activeTab === 'processed') fetchProcessed();
    if (activeTab === 'vehicles') fetchVehicleLookups();
  }, [activeTab, fetchProcessed, fetchVehicleLookups]);

  const handleProcess = async () => {
    setProcessing(true);
    try {
      const res = await apiFetch('/process', { method: 'POST' });
      await Promise.all([fetchLive(), fetchStats()]);
      alert(`✅ Processed\n• Aircraft: ${res.stats?.aircraft ?? 0}\n• Vehicles: ${res.stats?.vehicles ?? 0}\n• Errors: ${res.stats?.errors ?? 0}`);
    } catch (e) {
      alert(`❌ Error: ${e.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const filteredFeed = liveFeed.filter(d => {
    if (filter.entity && d.entity_type !== filter.entity) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (d.tail_number || '').toLowerCase().includes(q) ||
        (d.plate_number || '').toLowerCase().includes(q) ||
        (d.camera_name || '').toLowerCase().includes(q) ||
        (d.owner_name || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const t = stats?.totals || {};

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%',
      color: '#e2e8f0', fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
      fontSize: 13,
    }}>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: #0f172a; }
        ::-webkit-scrollbar-thumb { background: #1e293b; border-radius: 3px; }
      `}</style>

      <div style={{
        padding: '14px 20px',
        borderBottom: '1px solid #1e293b',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 8, height: 8, borderRadius: '50%',
            background: liveOn ? '#4ade80' : '#475569',
            boxShadow: liveOn ? '0 0 8px #4ade80' : 'none',
            animation: liveOn ? 'pulse 2s infinite' : 'none',
          }} />
          <div>
            <h1 style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9', margin: 0, letterSpacing: '0.02em' }}>
              Detection Stream
            </h1>
            <p style={{ fontSize: 10, color: '#475569', margin: 0 }}>
              Agent visual surveillance · ADS-B correlation
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => setLiveOn(v => !v)}
            style={{
              padding: '6px 12px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
              border: `1px solid ${liveOn ? '#4ade8040' : '#47556940'}`,
              background: liveOn ? 'rgba(74,222,128,0.08)' : 'rgba(71,85,105,0.15)',
              color: liveOn ? '#4ade80' : '#64748b',
              display: 'flex', alignItems: 'center', gap: 5,
            }}
          >
            {liveOn ? <Wifi style={{ width: 12, height: 12 }} /> : <WifiOff style={{ width: 12, height: 12 }} />}
            {liveOn ? 'Live' : 'Paused'}
          </button>

          <button
            onClick={handleProcess}
            disabled={processing}
            style={{
              padding: '6px 12px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
              border: '1px solid rgba(251,191,36,0.3)',
              background: 'rgba(251,191,36,0.08)',
              color: '#fbbf24',
              display: 'flex', alignItems: 'center', gap: 5,
              opacity: processing ? 0.6 : 1,
            }}
          >
            <Zap style={{ width: 12, height: 12 }} />
            {processing ? 'Processing…' : 'Process Now'}
          </button>

          <button
            onClick={() => { fetchLive(); fetchStats(); }}
            style={{
              padding: '6px 10px', borderRadius: 6, cursor: 'pointer',
              border: '1px solid #1e293b', background: 'rgba(30,41,59,0.5)',
              color: '#64748b',
            }}
          >
            <RefreshCw style={{ width: 13, height: 13 }} />
          </button>
        </div>
      </div>

      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 10, padding: '14px 20px',
          borderBottom: '1px solid #0f172a',
          flexShrink: 0,
        }}>
          <KpiCard label="Total (7d)"   value={t.total}         icon={Activity}  color="#60a5fa" pulse={liveOn} />
          <KpiCard label="Aircraft"     value={t.aircraft_count} icon={Plane}     color="#60a5fa" />
          <KpiCard label="Vehicles"     value={t.vehicle_count}  icon={Car}       color="#a78bfa" />
          <KpiCard label="ADS-B Match"  value={t.adsb_confirmed} icon={Wifi}      color="#22d3ee"
                   sub={t.total ? `${Math.round((t.adsb_confirmed / t.aircraft_count || 0) * 100)}% of A/C` : null} />
          <KpiCard label="Gates Opened" value={t.gates_opened}   icon={Shield}    color="#4ade80" />
          <KpiCard label="Pending"      value={t.pending}        icon={Clock}     color="#fbbf24"
                   sub={t.pending > 0 ? 'needs processing' : 'all clear'} />
          <KpiCard label="Avg Confidence" value={t.avg_confidence ? `${t.avg_confidence}%` : '—'}
                   icon={Eye} color="#fb923c" />
        </div>
      )}

      <div style={{
        display: 'flex', gap: 0,
        borderBottom: '1px solid #1e293b',
        padding: '0 20px', flexShrink: 0,
      }}>
        {[
          { id: 'live',      label: 'Live Feed',         count: filteredFeed.length },
          { id: 'processed', label: 'Grouped Events',    count: processed.length },
          { id: 'vehicles',  label: 'Vehicle Lookups',   count: vehicleLookups.length },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 16px', background: 'none', border: 'none',
              cursor: 'pointer', fontSize: 12, fontFamily: 'inherit',
              color: activeTab === tab.id ? '#e2e8f0' : '#475569',
              borderBottom: activeTab === tab.id ? '2px solid #60a5fa' : '2px solid transparent',
              display: 'flex', alignItems: 'center', gap: 6,
              transition: 'color 0.2s',
            }}
          >
            {tab.label}
            {tab.count > 0 && (
              <span style={{
                fontSize: 10, background: activeTab === tab.id ? '#1e3a5f' : '#1e293b',
                color: activeTab === tab.id ? '#60a5fa' : '#475569',
                padding: '1px 6px', borderRadius: 10,
              }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'live' && (
        <div style={{
          display: 'flex', gap: 8, padding: '10px 20px',
          borderBottom: '1px solid #0f172a', flexShrink: 0,
          alignItems: 'center',
        }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 260 }}>
            <Search style={{
              position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)',
              width: 12, height: 12, color: '#475569',
            }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search tail, plate, camera…"
              style={{
                width: '100%', background: '#0f172a',
                border: '1px solid #1e293b', borderRadius: 6,
                padding: '6px 8px 6px 28px', color: '#e2e8f0',
                fontSize: 11, fontFamily: 'inherit', outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {['', 'aircraft', 'vehicle'].map(type => (
            <button
              key={type}
              onClick={() => setFilter(f => ({ ...f, entity: type }))}
              style={{
                padding: '5px 10px', borderRadius: 5, fontSize: 11,
                cursor: 'pointer', fontFamily: 'inherit',
                border: `1px solid ${filter.entity === type ? '#60a5fa40' : '#1e293b'}`,
                background: filter.entity === type ? 'rgba(96,165,250,0.1)' : 'rgba(15,23,42,0.5)',
                color: filter.entity === type ? '#60a5fa' : '#475569',
              }}
            >
              {type === '' ? 'All' : type === 'aircraft' ? '✈ Aircraft' : '🚗 Vehicle'}
            </button>
          ))}

          <span style={{ fontSize: 10, color: '#334155', marginLeft: 'auto' }}>
            {filteredFeed.length} / {liveFeed.length}
          </span>
        </div>
      )}

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {activeTab === 'live' && (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '18px 1fr 100px 80px 90px 80px',
              gap: '0 12px', padding: '7px 14px',
              borderBottom: '1px solid #1e293b',
              fontSize: 10, color: '#334155',
              textTransform: 'uppercase', letterSpacing: '0.06em',
              position: 'sticky', top: 0, background: 'rgba(2,6,23,0.95)',
              zIndex: 1,
            }}>
              <span />
              <span>Identifier / Owner</span>
              <span>Camera</span>
              <span>Confidence</span>
              <span>Status</span>
              <span>Time</span>
            </div>

            {loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#334155' }}>
                Loading stream…
              </div>
            ) : filteredFeed.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#334155' }}>
                No detections{search ? ` matching "${search}"` : ''}
              </div>
            ) : (
              filteredFeed.map(det => (
                <LiveRow
                  key={det.id}
                  det={det}
                  isNew={newIds.has(det.id)}
                  onSelect={setSelected}
                />
              ))
            )}
          </div>
        )}

        {activeTab === 'processed' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px' }}>
            {processed.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#334155' }}>
                No grouped events yet — run processor first
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {processed.map(ev => (
                  <div
                    key={ev.id}
                    style={{
                      background: 'rgba(15,23,42,0.6)',
                      border: '1px solid #1e293b', borderRadius: 8,
                      padding: '12px 14px',
                      display: 'grid',
                      gridTemplateColumns: '20px 140px 1fr 120px 120px',
                      gap: '0 14px', alignItems: 'center',
                    }}
                  >
                    <EntityIcon type={ev.entity_type} size={14} />
                    <div>
                      <div style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700,
                        color: ev.entity_type === 'aircraft' ? '#60a5fa' : '#a78bfa' }}>
                        {ev.identifier}
                      </div>
                      <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>
                        {ev.detection_count} detect. · avg {Math.round(ev.avg_confidence || 0)}%
                      </div>
                    </div>
                    <div>
                      {ev.owner_name && (
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>{ev.owner_name}</div>
                      )}
                      <div style={{ fontSize: 10, color: '#334155', marginTop: 2 }}>
                        {formatTs(ev.first_seen)} → {formatTs(ev.last_seen)}
                      </div>
                    </div>
                    <StatusBadge status={ev.status} />
                    <div style={{ fontSize: 10, color: '#334155', textAlign: 'right' }}>
                      {ev.confirmed_movement_type && (
                        <div style={{ color: '#22d3ee', marginBottom: 2 }}>
                          ↓ ADS-B {ev.confirmed_movement_type}
                        </div>
                      )}
                      {ev.gate_authorized && (
                        <div style={{ color: '#4ade80' }}>🔓 Gate OK</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'vehicles' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px' }}>
            {vehicleLookups.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#334155' }}>
                No vehicle lookups yet
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '100px 80px 80px 60px 70px 80px 1fr 80px',
                  gap: '0 12px', padding: '6px 10px',
                  fontSize: 10, color: '#334155',
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                  borderBottom: '1px solid #1e293b',
                }}>
                  <span>Plate</span><span>Make</span><span>Model</span>
                  <span>Year</span><span>Color</span><span>Type</span>
                  <span>Owner</span><span>API</span>
                </div>
                {vehicleLookups.map(v => (
                  <div
                    key={v.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '100px 80px 80px 60px 70px 80px 1fr 80px',
                      gap: '0 12px', padding: '8px 10px',
                      background: 'rgba(15,23,42,0.5)',
                      borderRadius: 6, border: '1px solid #1e293b',
                      fontSize: 12, alignItems: 'center',
                    }}
                  >
                    <span style={{ fontFamily: 'monospace', color: '#a78bfa', fontWeight: 700 }}>
                      {v.plate_number}
                    </span>
                    <span style={{ color: '#94a3b8' }}>{v.make || '—'}</span>
                    <span style={{ color: '#94a3b8' }}>{v.model || '—'}</span>
                    <span style={{ color: '#94a3b8' }}>{v.year || '—'}</span>
                    <span style={{ color: '#94a3b8' }}>{v.color || '—'}</span>
                    <span style={{ color: '#94a3b8' }}>{v.vehicle_type || '—'}</span>
                    <span style={{ color: v.owner_name ? '#4ade80' : '#334155' }}>
                      {v.owner_name || 'Unknown'}
                    </span>
                    <span style={{
                      fontSize: 10, color: '#475569',
                      background: '#1e293b', padding: '2px 6px', borderRadius: 4,
                    }}>
                      {v.api_provider || '—'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {selected && (
          <DetailPanel det={selected} onClose={() => setSelected(null)} />
        )}
      </div>

      <div style={{
        padding: '6px 20px',
        borderTop: '1px solid #0f172a',
        display: 'flex', alignItems: 'center', gap: 16,
        fontSize: 10, color: '#334155', flexShrink: 0,
        background: 'rgba(2,6,23,0.6)',
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%',
            background: liveOn ? '#4ade80' : '#475569',
            display: 'inline-block',
          }} />
          {liveOn ? `Polling every ${POLL_INTERVAL / 1000}s` : 'Polling paused'}
        </span>
        {stats && (
          <>
            <span>·</span>
            <span>{t.total || 0} total detections (7d)</span>
            <span>·</span>
            <span>{t.images_cleaned || 0} images cleaned</span>
          </>
        )}
        <span style={{ marginLeft: 'auto' }}>
          {new Date().toLocaleTimeString()}
        </span>
      </div>
    </div>
  );
};

export default DetectionsViewer;